import React from "react";
import { Link } from "react-router-dom";
import {
  Coffee,
  Footprints,
  UtensilsCrossed,
  BookOpen,
  Bike,
  Camera,
  Music,
  Trees,
} from "lucide-react";
import Reveal from "./Reveal";
import Doodle from "./Doodle";
import Highlighter from "./Highlighter";

// Act 3b: what a loop actually is, as two counter-scrolling bands.
//
// This replaces a static six-cell grid. The grid was accurate and completely
// inert, which was the fair criticism of the first pass: correct structure,
// nothing happening. Two rows drifting in opposite directions give the section
// life without asking the reader to do anything, and it lets the list read as
// "there is always something on" rather than "here are exactly six options".
//
// WHY NOT THE LIVE FEED (unchanged from the grid version).
// Sampling /api/events on 2026-07-31: 890 of 1000 rows are kind "other", and
// the top neighbourhoods are Cardiff, Edinburgh, Birmingham, Sheffield,
// Nottingham, Barcelona, Jakarta and Ann Arbor. London is not in the top eight,
// and the first entries are scraped third-party listings (a corporate lunch in
// Munich, tech meetups in Kuala Lumpur). Rendering that under a hero reading
// "London, and growing" would contradict the hero on the same screen. These are
// therefore ACTIVITY TYPES, with no invented attendee counts, times or venues,
// because inventing those is fabricating social proof. Rewire when the London
// feed is dense and vetted.
//
// Motion is CSS-only (no JS, no rAF loop) and is disabled wholesale under
// prefers-reduced-motion, where it degrades to a plain static row.

type Item = { label: string; Icon: typeof Coffee; tint: string };

const ROW_A: Item[] = [
  { label: "A coffee", Icon: Coffee, tint: "var(--color-paper-salmon)" },
  { label: "A morning run", Icon: Footprints, tint: "var(--color-paper-mint)" },
  {
    label: "Dinner with strangers",
    Icon: UtensilsCrossed,
    tint: "var(--color-paper-sand)",
  },
  { label: "Book club", Icon: BookOpen, tint: "var(--color-paper-salmon)" },
];
const ROW_B: Item[] = [
  { label: "A 6:30 kickabout", Icon: Bike, tint: "var(--color-paper-mint)" },
  { label: "A gallery wander", Icon: Camera, tint: "var(--color-paper-sand)" },
  { label: "Someone's gig", Icon: Music, tint: "var(--color-paper-salmon)" },
  { label: "A park picnic", Icon: Trees, tint: "var(--color-paper-mint)" },
];

const Chip: React.FC<{ item: Item }> = ({ item }) => {
  const { Icon } = item;
  return (
    <span
      className="inline-flex items-center gap-3 rounded-full border border-hairline/50 px-6 py-4 mr-4 whitespace-nowrap"
      style={{ background: item.tint }}
    >
      <Icon size={20} className="text-salmon-dark" aria-hidden="true" />
      <span className="font-semibold text-ink text-[15px] md:text-base">
        {item.label}
      </span>
    </span>
  );
};

// Duplicated once so the strip can translate a full -50% and loop seamlessly.
const Row: React.FC<{ items: Item[]; reverse?: boolean }> = ({
  items,
  reverse,
}) => (
  <div className="ll-marquee">
    <div className={`ll-track ${reverse ? "ll-track-rev" : ""}`}>
      {[...items, ...items, ...items, ...items].map((it, i) => (
        <Chip key={`${it.label}-${i}`} item={it} />
      ))}
    </div>
  </div>
);

const LoopMarquee: React.FC = () => (
  <section className="relative w-full bg-cream py-24 md:py-32 overflow-hidden">
    {/* .ll-marquee / .ll-track live in styles.css, so /about can use
        the same band without this component being mounted. */}

    <Doodle
      name="star"
      size={44}
      rotate={-14}
      className="absolute w-8 h-8 md:w-auto md:h-auto left-[7%] top-[12%] text-salmon-dark/55"
    />
    <Doodle
      name="squiggle"
      size={86}
      className="absolute w-8 h-8 md:w-auto md:h-auto right-[8%] bottom-[14%] text-teal/40"
    />

    <div className="max-w-3xl mx-auto text-center mb-12 px-6">
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-salmon-dark mb-4">
        What you would turn up to
      </p>
      <Reveal y={20}>
        <h2
          className="font-headline text-ink tracking-[-0.025em] leading-[1.16] text-[clamp(2rem,5.8vw,3.6rem)] text-balance"
          style={{ fontWeight: 700 }}
        >
          The activity is the excuse.{" "}
          <Highlighter tone="mint">The people are the point.</Highlighter>
        </h2>
      </Reveal>
      <Reveal y={14} delay={110}>
        <p className="mt-5 text-lg text-ink/70 leading-relaxed">
          A loop is any real-world plan worth having company for. Small, local,
          and usually this week.
        </p>
      </Reveal>
    </div>

    <div className="flex flex-col gap-4">
      <Row items={ROW_A} />
      <Row items={ROW_B} reverse />
    </div>

    <Reveal y={14} delay={140}>
      <p className="mt-12 text-center text-ink/60 px-6">
        Whatever you were going to do anyway.{" "}
        <Link
          to="/how-it-works"
          className="text-salmon-dark font-semibold border-b-2 border-salmon/40 hover:border-salmon transition-colors"
        >
          See how it works
        </Link>
      </p>
    </Reveal>
  </section>
);

export default LoopMarquee;
